import { Node, NodeProps } from "@xyflow/react"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { Play, Plus, Rocket, Split, Trash } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { WorkflowEventEnum } from "schema"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { EventIconMap } from "../maps/eventIconMap"
import { WorkflowNode } from "./WorkflowNode"

export function EventNode({ data, type, ...props }: NodeProps<Node>) {
  const { t } = useTranslation()
  const { onAddConditionNode, onAddActionNode, onDeleteNode, setFocusedNode } = useWorkflowBuilder()
  const [isOpen, setIsOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { form } = useWorkflowBuilder()

  const nodeIndex = useMemo(
    () => ensure(form.watch("nodes")?.findIndex((node) => node.id === props?.id)),
    [form, props.id],
  )
  const selectedEvent = form.watch(`nodes.${nodeIndex}.event`)
  const selectedConfig = form.watch(`nodes.${nodeIndex}.config`)
  const selectedGoal = selectedConfig?.goal
  const selectedCapabilityId = selectedConfig?.capabilityId

  const isComplete = useMemo(() => {
    switch (selectedEvent) {
      case WorkflowEventEnum.ConversationGoalAchieved:
        return !!selectedEvent && !!selectedGoal
      case WorkflowEventEnum.CapabilityEvaluated:
      case WorkflowEventEnum.CapabilityExecuted:
        return !!selectedEvent && !!selectedCapabilityId
      default:
        return !!selectedEvent
    }
  }, [selectedEvent, selectedGoal, selectedCapabilityId])

  function handleAddConditionNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsOpen(false)
    setTimeout(() => {
      onAddConditionNode(props.id, "event")
    }, 100)
  }

  function handleAddActionNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsOpen(false)
    setTimeout(() => {
      onAddActionNode(props.id, "event")
    }, 100)
  }

  function onRequestDeleteNode(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setIsDeleteDialogOpen(true)
  }

  function onConfirmDeleteNode() {
    setIsDeleteDialogOpen(false)
    onDeleteNode(props.id)
  }

  function onCancelDeleteNode() {
    setIsDeleteDialogOpen(false)
    setFocusedNode(props.id)
  }

  const description = useMemo(() => {
    if (!selectedEvent) return t("campaigns.event.title")

    const selectedEventLabel = t(`campaigns.event.options.${selectedEvent}.label`)
    const SelectedEventIcon = EventIconMap[selectedEvent]

    return (
      <div className="flex items-center gap-2">
        <SelectedEventIcon className="w-4 h-4 text-chart-primary" />
        <span className="text-base">{selectedEventLabel}</span>
      </div>
    )
  }, [selectedEvent, t])

  return (
    <>
      <WorkflowNode
        {...props}
        data={data}
        type={type}
        labels={[
          <Badge key="event" variant="outline">
            <Play className="w-4 h-4" />
            {t("campaigns.event.label")}
          </Badge>,
        ]}
        description={description}
        isComplete={isComplete}
        addButton={
          <Popover open={isOpen} onOpenChange={setIsOpen} modal>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className={cn(
                  "absolute group-[.node-open]:border-chart-primary rounded-full -bottom-5 right-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 group-[.node-open]:opacity-100 transition-all",
                  isOpen && "opacity-100",
                )}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setIsOpen(true)
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="center" className="p-1">
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  onClick={handleAddConditionNode}
                  className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                >
                  <Split className="w-4 h-4 text-chart-primary" />
                  {t("campaigns.condition.label")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleAddActionNode}
                  className="justify-start items-center py-2 px-3 gap-2 min-w-32 w-auto h-auto"
                >
                  <Rocket className="w-4 h-4 text-chart-primary" />
                  {t("campaigns.action.label")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        }
        deleteButton={
          <Button
            size="icon"
            variant="ghost"
            onClick={onRequestDeleteNode}
            className="absolute w-8 h-8 right-1 bottom-1 hidden group-hover:flex group-[.node-open]:flex"
          >
            <Trash className="w-4 h-4" />
          </Button>
        }
      />
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("campaigns.event.deleteDialog.title")}</DialogTitle>
            <DialogDescription>{t("campaigns.event.deleteDialog.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onCancelDeleteNode}>
              {t("shared.cancel")}
            </Button>
            <Button variant="destructive" onClick={onConfirmDeleteNode}>
              {t("campaigns.event.deleteDialog.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
